/**
 * <ma-media-arch>: PDP reel arch (Figma 955:849 desktop / 1069:2503 mobile), as an endless loop.
 * Motion lives in assets/ma-reel-loop.js: card height follows the distance from the centre
 * continuously (Figma 444 / 384 / 325 / 266, piecewise linear in between), so the arch reshapes
 * itself while it moves, whether that's a drag, a swipe, a trackpad scroll or auto-advance.
 *
 * - Auto-advance: the featured video plays to the end (image cards stay STILL_MS), then the arch moves on.
 *   It waits while the pointer is over the arch, the arch is moving or the reel pop-up is open, and stops
 *   off-screen / with reduced motion.
 * - Click/tap a side card to bring it to the centre; click/tap the featured card to open the reel pop-up
 *   (Shop Now lives there, client feedback R1). Arrow keys move between cards.
 * - Hover (mouse) previews a video; the "Drag" label follows the pointer.
 */
if (!customElements.get('ma-media-arch')) {
  const RATIO = 250 / 444; // card width / height (Figma)
  const STILL_MS = 6000;

  class MaMediaArch extends HTMLElement {
    connectedCallback() {
      if (window.MaReelLoop) this.init();
      else document.addEventListener('ma-reel-loop:ready', () => this.init(), { once: true });
    }

    init() {
      this.track = this.querySelector('.ma-media-arch__track');
      if (!this.track || this.track.dataset.loopReady) return;
      this.cards = Array.from(this.track.querySelectorAll('.ma-media-arch__card'));
      const n = this.cards.length;
      if (!n) return;
      this.track.dataset.loopReady = '1';
      this.track.classList.add('is-loop');
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.active = -1; this.inView = false; this.hovering = false; this.modal = false; this.timer = 0;
      this.cards.forEach((c) => c.querySelectorAll('img, video').forEach((m) => m.setAttribute('draggable', 'false')));

      let hs = [444, 384, 325, 266];
      const hAt = (u) => { const k = Math.min(3, Math.floor(u)), f = Math.min(1, u - k); return hs[k] + ((hs[Math.min(3, k + 1)]) - hs[k]) * f; };
      this.loop = window.MaReelLoop(this.track, this.cards, {
        start: Math.floor((n - 1) / 2), // the middle card, like the Figma arch
        measure: () => {
          const cs = getComputedStyle(this.track);
          hs = [0, 1, 2, 3].map((d) => parseFloat(cs.getPropertyValue('--ma-arch-h' + d)) || 266);
          return { widths: hs.map((h) => h * RATIO), gap: parseFloat(cs.columnGap) || 20 };
        },
        render: (el, d, u) => {
          el.style.height = hAt(u).toFixed(2) + 'px';
          el.style.setProperty('--wash', Math.min(1, u).toFixed(3)); // side cards washed out, as in Figma
        },
        onIndex: (i, prev) => {
          if (prev > -1) { this.cards[prev].classList.remove('is-active'); const o = this.vid(prev); if (o) { o.pause(); o.muted = true; } }
          this.active = i;
          this.cards[i].classList.add('is-active');
          const v = this.vid(i); if (v) { v.currentTime = 0; if (this.inView && !this.modal) this.play(v); }
          this.syncSound();
        },
        onSettle: () => this.schedule(),
      });

      this.initDragLabel();
      this.initCards();
      this.initDialog();
      this.initSound();

      if (this.canHover) {
        this.addEventListener('mouseenter', () => { this.hovering = true; });
        this.addEventListener('mouseleave', () => { this.hovering = false; });
      }
      new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.schedule(); else this.pauseAll();
      }, { threshold: 0.35 }).observe(this.track);
      this.closest('.shopify-section')?.addEventListener('shopify:block:select', (e) => {
        const i = this.cards.indexOf(e.target.closest('.ma-media-arch__card') || e.target);
        if (i > -1) this.loop.goTo(i);
      });
    }

    vid(i) { return this.cards[i] && this.cards[i].querySelector('video'); }
    play(v) { const p = v.play(); if (p) p.catch(() => {}); }
    pauseAll() { clearTimeout(this.timer); this.cards.forEach((c, i) => { const v = this.vid(i); if (v) v.pause(); }); }

    schedule() {
      clearTimeout(this.timer);
      if (!this.inView || this.modal || this.active < 0) return;
      const v = this.vid(this.active);
      if (v) { if (v.paused) this.play(v); }
      else if (!this.reduce) this.timer = setTimeout(() => this.next(), STILL_MS);
    }
    next() {
      if (this.hovering || this.modal || this.loop.busy()) { this.schedule(); return; }
      this.loop.next();
    }

    initCards() {
      const n = this.cards.length;
      this.cards.forEach((card, i) => {
        const v = this.vid(i);
        if (v) {
          v.loop = false; v.muted = true;
          v.addEventListener('timeupdate', () => { if (v.duration) card.style.setProperty('--ma-arch-progress', (v.currentTime / v.duration).toFixed(3)); });
          v.addEventListener('ended', () => {
            if (i !== this.active || this.reduce || this.hovering) { v.currentTime = 0; this.play(v); return; } // previews loop; featured replays while hovered
            this.next();
          });
          if (this.canHover) {
            card.addEventListener('mouseenter', () => { if (i !== this.active) this.play(v); });
            card.addEventListener('mouseleave', () => { if (i !== this.active) v.pause(); });
          }
        }
        const open = card.querySelector('.ma-media-arch__open');
        if (!open) return;
        // Side card: bring it to the centre. Featured card: open the reel pop-up.
        open.addEventListener('click', () => { if (i === this.active && !this.loop.busy()) this.openReel(card); else this.loop.goTo(i); });
        open.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const j = (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
          this.loop.goTo(j);
          this.cards[j].querySelector('.ma-media-arch__open')?.focus({ preventScroll: true });
        });
      });
    }

    // "Drag" label follows the pointer over the arch (desktop).
    initDragLabel() {
      const label = document.createElement('span');
      label.className = 'ma-media-arch__drag';
      label.textContent = this.dataset.dragLabel || 'Drag';
      label.setAttribute('aria-hidden', 'true');
      this.appendChild(label);
      this.addEventListener('pointermove', (e) => {
        const r = this.getBoundingClientRect();
        label.style.transform = `translate(${e.clientX - r.left}px, ${e.clientY - r.top}px)`;
      });
    }

    // Reel pop-up: full size, sound on, with the Shop Now button.
    initDialog() {
      this.dialog = this.querySelector('.ma-reel');
      if (!this.dialog) return;
      this.dialogBox = this.dialog.querySelector('.ma-reel__media');
      this.dialogInfo = this.dialog.querySelector('.ma-reel__info');
      this.dialogFrame = this.dialog.querySelector('.ma-reel__frame');
      this.dialogSound = this.dialog.querySelector('.ma-reel__sound');
      this.reelMuted = false;
      this.dialog.querySelector('.ma-reel__close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
      this.dialog.addEventListener('close', () => { this.dialogBox.replaceChildren(); this.modal = false; this.schedule(); });
      this.dialog.querySelector('.ma-reel__prev').addEventListener('click', () => this.stepReel(-1));
      this.dialog.querySelector('.ma-reel__next').addEventListener('click', () => this.stepReel(1));
      this.dialog.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.stepReel(e.key === 'ArrowRight' ? 1 : -1); }
      });
      // Swipe left/right on the open reel to move between reels.
      let sx = 0, sy = 0;
      this.dialogFrame.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
      this.dialogFrame.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) { this.swiped = true; this.stepReel(dx < 0 ? 1 : -1); }
      });
      // Tap the video to pause / play (our own controls; the native iOS ones covered the close button).
      this.dialogBox.addEventListener('click', () => {
        if (this.swiped) { this.swiped = false; return; }
        const v = this.dialogBox.querySelector('video');
        if (v) { if (v.paused) this.play(v); else v.pause(); }
      });
      this.dialogSound.addEventListener('click', () => {
        const v = this.dialogBox.querySelector('video');
        if (!v) return;
        this.reelMuted = v.muted = !v.muted;
        if (v.paused) this.play(v);
        this.syncReelSound(v);
      });
    }
    stepReel(dir) {
      const n = this.cards.length;
      if (n < 2) return;
      const j = (this.reelIdx + dir + n) % n;
      this.loop.goTo(j); // keep the arch behind in step
      this.openReel(this.cards[j]);
    }
    syncReelSound(v) {
      this.dialogSound.hidden = !v;
      if (!v) return;
      this.dialogSound.setAttribute('aria-pressed', String(!v.muted));
      this.dialogSound.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
    }
    openReel(card) {
      if (!this.dialog) return;
      const media = card.querySelector('.ma-media-arch__media');
      if (!media) return;
      this.reelIdx = this.cards.indexOf(card);
      const el = media.cloneNode(true);
      el.className = 'ma-reel__el';
      el.removeAttribute('style');
      this.dialogBox.replaceChildren(el);
      this.dialogInfo.replaceChildren(...[...card.querySelectorAll('.ma-media-arch__title, .ma-media-arch__blurb, .ma-media-arch__cta')].map((x) => x.cloneNode(true)));
      this.modal = true;
      this.pauseAll();
      const box = this.dialogFrame;
      if (el.tagName === 'VIDEO') {
        // Poster first so it never opens on a dark frame; spinner until frames actually play.
        const poster = media.getAttribute('poster') || media.dataset.poster;
        if (poster) el.poster = poster;
        el.controls = false; el.loop = true; el.playsInline = true; el.preload = 'auto'; el.muted = this.reelMuted;
        box.classList.add('is-loading'); box.classList.remove('is-paused');
        el.addEventListener('playing', () => box.classList.remove('is-loading', 'is-paused'));
        el.addEventListener('waiting', () => box.classList.add('is-loading'));
        el.addEventListener('pause', () => box.classList.add('is-paused'));
        el.addEventListener('timeupdate', () => { if (el.duration) box.style.setProperty('--p', (el.currentTime / el.duration).toFixed(3)); });
        el.play().catch(() => { el.muted = true; this.syncReelSound(el); el.play().catch(() => box.classList.add('is-paused')); });
        this.syncReelSound(el);
      } else {
        box.classList.remove('is-loading', 'is-paused');
        this.syncReelSound(null);
      }
      if (!this.dialog.open) this.dialog.showModal();
    }

    // Sound button: only the featured card's video can be unmuted. Pressing it on a side card brings that card in.
    initSound() {
      this.cards.forEach((card, i) => {
        const btn = card.classList.contains('has-video') && card.querySelector('.ma-media-arch__sound');
        const v = this.vid(i);
        if (!btn || !v) return;
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          if (i !== this.active) { this.loop.goTo(i); return; }
          v.muted = !v.muted;
          if (v.paused) this.play(v);
          this.syncSound();
        });
      });
    }
    syncSound() {
      this.cards.forEach((card, i) => {
        const v = this.vid(i), btn = card.querySelector('.ma-media-arch__sound');
        if (!v || !btn) return;
        if (i !== this.active) v.muted = true;
        btn.setAttribute('aria-pressed', String(!v.muted));
        btn.setAttribute('aria-label', v.muted ? 'Unmute video' : 'Mute video');
      });
    }
  }
  customElements.define('ma-media-arch', MaMediaArch);
}
